/**
 * The KPI grid: rows are metrics grouped exactly as the metric pack groups
 * them, columns are periods.
 *
 * The table IS the deliverable of this epic, so the layout is built around
 * reading a number and then checking it: the metric column is sticky (a value
 * eight months across still has its label), the header row is sticky (it still
 * has its month), and clicking a value goes STRAIGHT to the document behind it
 * — the same viewer popup a chat citation opens, with the cited cell
 * highlighted.
 *
 * Only a number with several sources behind it stops at an intermediate step:
 * there the click has a real question to answer (which of these inputs?), and
 * the in-grid expansion asks it with the formula and the component list. A
 * number with one source, or with one input standing behind it, opens that
 * source directly — a menu of one is a click that asks nothing.
 *
 * One expansion at a time: two open accordions push the second one's row so far
 * down that neither number is next to its own breakdown any more.
 *
 * Only the horizontal axis scrolls independently — periods grow without bound
 * as months are added, metrics do not.
 *
 * Rows that hold no number in any column can be hidden (`hideEmptyRows`,
 * DOP-293; the page shows them by default since DOP-609). Shown, they are
 * muted and each states why it is blank — a metric
 * that was searched for and not found is data about the run, and a grid that
 * drops it silently makes every real blank look like a fault.
 */

import { GripVertical } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
	KpiOverrideOutcome,
	KpiRevertOutcome,
} from "../../services/kpiService";
import {
	type KpiMetricGroup,
	type KpiTable,
	isCustomMetricRow,
} from "../../types/kpi";
import KpiCellExpansion from "./KpiCellExpansion";
import KpiCellView from "./KpiCellView";
import KpiCustomMetricDelete from "./KpiCustomMetricDelete";
import KpiCustomMetricEdit from "./KpiCustomMetricEdit";
import { formatPeriod } from "./formatKpiValue";
import {
	EMPTY_ROW_MESSAGE,
	isEmptyMetricRow,
	metricGroupsWithoutEmptyRows,
} from "./kpiEmptyRow";
import { kpiCellAction } from "./kpiCellAction";
import type { KpiMetricDrop } from "./kpiLayout";
import { useKpiSourceOpener } from "./useKpiSourceOpener";

export interface KpiCellCoords {
	metricKey: string;
	period: string;
}

export interface KpiGridProps {
	table: KpiTable;
	/** The cell whose evidence is open in the grid, if any. */
	expanded: KpiCellCoords | null;
	/** Clicking a value. Passing null closes the open expansion. */
	onExpandCell: (coords: KpiCellCoords | null) => void;
	/** Secondary: the metadata/override/ask panel, from inside the expansion. */
	onOpenDetails: (coords: KpiCellCoords) => void;
	/** Correcting a breakdown component in place. */
	onOverride?: (
		valueId: string,
		value: number,
	) => Promise<KpiOverrideOutcome>;
	/** Undoing a breakdown component's override in place. */
	onRevert?: (valueId: string) => Promise<KpiRevertOutcome>;
	/**
	 * Removing a user-defined row. Omitted when the page cannot resolve the
	 * definitions behind those rows — the grid then renders them read-only,
	 * which is the honest offer when the id a delete needs is unknown.
	 */
	onDeleteCustomMetric?: (metricKey: string) => void;
	/** Editing a user-defined row. Gated on the definitions for the same reason. */
	onEditCustomMetric?: (metricKey: string) => void;
	/** The custom row whose delete is in flight, if any. */
	deletingMetricKey?: string | null;
	/**
	 * Drop the rows that hold no number in any column (DOP-293). Defaults to
	 * TRUE — the grid as it has always looked. Turning it off is how a user asks
	 * "what did the run look for and not find?", and every row that then appears
	 * says why it is blank.
	 */
	hideEmptyRows?: boolean;
	/**
	 * Move a row (DOP-613). With it, each row and section heading gets a drag
	 * handle — mouse, or focus it and use the arrow keys. Without it the grid
	 * is in a fixed order.
	 */
	onMoveMetric?: (metricKey: string, drop: KpiMetricDrop) => void;
	onMoveSection?: (
		sectionKey: string,
		drop: { beforeKey?: string | null; afterKey?: string | null },
	) => void;
}

type DragItem = { kind: "metric" | "section"; key: string };
type DropHint =
	| { kind: "row"; key: string; where: "before" | "after" }
	| { kind: "section"; key: string; where: "before" | "after" | "into" };

/** Before or after an element, by which half of it the pointer is over. */
function halfOf(event: React.DragEvent<HTMLElement>): "before" | "after" {
	const rect = event.currentTarget.getBoundingClientRect();
	return event.clientY < rect.top + rect.height / 2 ? "before" : "after";
}

const HANDLE =
	"inline-flex h-5 w-4 shrink-0 cursor-grab items-center justify-center rounded text-text-subtle opacity-0 hover:bg-gray-200 hover:text-text-base focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-[#008782] active:cursor-grabbing";

const KpiGrid: React.FC<KpiGridProps> = ({
	table,
	expanded,
	onExpandCell,
	onOpenDetails,
	onOverride,
	onRevert,
	onDeleteCustomMetric,
	onEditCustomMetric,
	deletingMetricKey = null,
	hideEmptyRows = true,
	onMoveMetric,
	onMoveSection,
}) => {
	const openSource = useKpiSourceOpener();
	const scrollerRef = useRef<HTMLDivElement>(null);
	const [dragging, setDragging] = useState<DragItem | null>(null);
	const [dropHint, setDropHint] = useState<DropHint | null>(null);
	// The handle to put focus back on after a keyboard move re-renders the grid.
	const [refocus, setRefocus] = useState<string | null>(null);

	/**
	 * Which rows are user-defined, by key.
	 *
	 * Built once per table rather than re-derived per click, and it answers for
	 * the GROUP as well as the key prefix — a server that files a row under
	 * "custom" without minting a prefixed key still gets custom behaviour.
	 */
	const customMetricKeys = useMemo(() => {
		const keys = new Set<string>();
		table.groups.forEach((group) => {
			group.metrics.forEach((metric) => {
				if (isCustomMetricRow(metric.key, group.key)) keys.add(metric.key);
			});
		});
		return keys;
	}, [table]);

	/**
	 * The groups actually rendered.
	 *
	 * A group whose every row is empty is dropped WITH its rows: a section
	 * heading over nothing reads as a rendering fault, which is the impression
	 * this ticket exists to remove rather than relocate.
	 */
	const groups = useMemo<KpiMetricGroup[]>(() => {
		if (!hideEmptyRows) return table.groups;
		return metricGroupsWithoutEmptyRows(table.groups);
	}, [table.groups, hideEmptyRows]);

	/** The rows as drawn, top to bottom — what a keyboard move steps through. */
	const visibleRows = useMemo(
		() =>
			groups.flatMap((group) =>
				group.metrics.map((metric) => ({ key: metric.key, section: group.key })),
			),
		[groups],
	);

	/*
	 * An open cell is on screen. Opened from somewhere else (a Needs review
	 * row), it can be far down or across the grid; "nearest" leaves a cell
	 * that is already visible, e.g. one just clicked, exactly where it is.
	 */
	const expandedMetric = expanded?.metricKey;
	const expandedPeriod = expanded?.period;
	useEffect(() => {
		if (!expandedMetric || !expandedPeriod) return;
		scrollerRef.current
			?.querySelector<HTMLElement>("td[data-selected]")
			?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
	}, [expandedMetric, expandedPeriod]);

	useEffect(() => {
		if (!refocus) return;
		scrollerRef.current
			?.querySelector<HTMLElement>(`[data-move-handle="${refocus}"]`)
			?.focus();
		setRefocus(null);
	}, [refocus, groups]);

	const moveMetricByKey = useCallback(
		(event: React.KeyboardEvent, metricKey: string, section: string) => {
			if (!onMoveMetric) return;
			if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
			event.preventDefault();
			const index = visibleRows.findIndex((row) => row.key === metricKey);
			if (index < 0) return;
			if (event.key === "ArrowUp") {
				const prev = visibleRows[index - 1];
				if (!prev) return;
				// Within the section: above the row before. At its top: to the
				// end of the section above.
				onMoveMetric(
					metricKey,
					prev.section === section
						? { section, beforeKey: prev.key }
						: { section: prev.section, afterKey: prev.key },
				);
			} else {
				const next = visibleRows[index + 1];
				if (!next) return;
				onMoveMetric(
					metricKey,
					next.section === section
						? { section, afterKey: next.key }
						: { section: next.section, beforeKey: next.key },
				);
			}
			setRefocus(`m:${metricKey}`);
		},
		[onMoveMetric, visibleRows],
	);

	const moveSectionByKey = useCallback(
		(event: React.KeyboardEvent, sectionKey: string) => {
			if (!onMoveSection) return;
			if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
			event.preventDefault();
			const index = groups.findIndex((group) => group.key === sectionKey);
			if (event.key === "ArrowUp" && index > 0) {
				onMoveSection(sectionKey, { beforeKey: groups[index - 1].key });
			} else if (event.key === "ArrowDown" && index < groups.length - 1) {
				onMoveSection(sectionKey, { afterKey: groups[index + 1].key });
			} else {
				return;
			}
			setRefocus(`s:${sectionKey}`);
		},
		[onMoveSection, groups],
	);

	const endDrag = useCallback(() => {
		setDragging(null);
		setDropHint(null);
	}, []);

	const cellAt = useCallback(
		(metricKey: string, period: string) => {
			for (const group of table.groups) {
				const metric = group.metrics.find((m) => m.key === metricKey);
				if (metric) return metric.cells[period];
			}
			return undefined;
		},
		[table],
	);

	const handleSelect = useCallback(
		(metricKey: string, period: string) => {
			// A custom row goes STRAIGHT to the details panel. Its expansion
			// carried the formula and the inputs — which is exactly what the panel
			// shows for these cells — so the intermediate step asked a question
			// with one answer, the same reasoning `kpiCellAction` uses to collapse
			// a menu of one.
			if (customMetricKeys.has(metricKey)) {
				onExpandCell(null);
				onOpenDetails({ metricKey, period });
				return;
			}
			const action = kpiCellAction(cellAt(metricKey, period));
			if (action.kind === "open" && openSource(action.source, action.cited)) {
				// Straight to the document. An expansion left open behind the popup
				// would still be there when it closes, having answered nothing.
				onExpandCell(null);
				return;
			}
			// Clicking the open cell again closes it; clicking another moves the
			// expansion there.
			const isOpen =
				expanded?.metricKey === metricKey && expanded?.period === period;
			onExpandCell(isOpen ? null : { metricKey, period });
		},
		[cellAt, customMetricKeys, expanded, onExpandCell, onOpenDetails, openSource],
	);

	const handleOpenDetails = useCallback(
		(metricKey: string, period: string) => onOpenDetails({ metricKey, period }),
		[onOpenDetails],
	);

	const closeExpansion = useCallback(() => onExpandCell(null), [onExpandCell]);

	// The grain the SERVER aggregated into, verbatim and unnarrowed: the labels
	// have to describe the numbers actually on screen (during a refetch those
	// are still the previous grain's), and a bucket this build cannot label
	// must reach `formatPeriod` as itself so the header falls back to the raw
	// period key rather than to a month that is not there.
	const grain = table.period_grain;
	const periodLabels = useMemo(
		() => table.periods.map((period) => formatPeriod(period, grain)),
		[table.periods, grain],
	);

	return (
		// The grid's scroll container, and the only thing on this page allowed to
		// scroll sideways. `w-full min-w-0` stops the table's intrinsic width from
		// sizing this box: without it the box grows to fit the widest month range
		// and takes the page with it.
		<div
			ref={scrollerRef}
			data-testid="kpi-grid-scroller"
			className="min-h-0 w-full min-w-0 flex-1 overflow-auto rounded-[10px] border border-gray-200 bg-white"
		>
			<table className="w-max min-w-full border-collapse text-base">
				<thead className="sticky top-0 z-20 bg-[#fafafa]">
					<tr>
						<th className="sticky left-0 z-30 min-w-[240px] border-b border-gray-200 bg-[#fafafa] px-3 py-[9px] text-left text-xs font-semibold text-text-muted">
							Metric
						</th>
						{table.periods.map((period, index) => (
							<th
								key={period}
								scope="col"
								className="min-w-[104px] whitespace-nowrap border-b border-gray-200 bg-[#fafafa] px-3 py-[9px] text-right text-xs font-semibold text-text-muted"
							>
								{periodLabels[index]}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{groups.map((group) => (
						<React.Fragment key={group.key}>
							<tr
								className={`group ${
									dropHint?.kind === "section" && dropHint.key === group.key
										? dropHint.where === "into"
											? "[&>*]:bg-[#E6F3F3]"
											: dropHint.where === "before"
												? "[&>*]:shadow-[inset_0_2px_0_#008782]"
												: "[&>*]:shadow-[inset_0_-2px_0_#008782]"
										: ""
								}`}
								onDragOver={(event) => {
									if (!dragging) return;
									event.preventDefault();
									const where =
										dragging.kind === "metric" ? "into" : halfOf(event);
									if (
										dropHint?.kind !== "section" ||
										dropHint.key !== group.key ||
										dropHint.where !== where
									) {
										setDropHint({ kind: "section", key: group.key, where });
									}
								}}
								onDrop={(event) => {
									event.preventDefault();
									if (dragging?.kind === "metric") {
										// Onto a heading: to the top of that section.
										onMoveMetric?.(dragging.key, {
											section: group.key,
											beforeKey: group.metrics[0]?.key ?? null,
										});
									} else if (dragging?.kind === "section") {
										onMoveSection?.(
											dragging.key,
											halfOf(event) === "before"
												? { beforeKey: group.key }
												: { afterKey: group.key },
										);
									}
									endDrag();
								}}
							>
								<th
									scope="colgroup"
									colSpan={table.periods.length + 1}
									className="sticky left-0 border-b border-gray-100 bg-[#f7f7f6] px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-[.08em] text-text-subtle"
								>
									<span className="flex items-center gap-1">
										{onMoveSection && (
											<button
												type="button"
												draggable
												data-move-handle={`s:${group.key}`}
												aria-label={`Move section ${group.label}`}
												title="Drag to move this section, or use the ↑ ↓ keys"
												onDragStart={(event) => {
													event.dataTransfer.effectAllowed = "move";
													event.dataTransfer.setData("text/plain", group.key);
													setDragging({ kind: "section", key: group.key });
												}}
												onDragEnd={endDrag}
												onKeyDown={(event) => moveSectionByKey(event, group.key)}
												className={HANDLE}
											>
												<GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
											</button>
										)}
										{group.label}
									</span>
								</th>
							</tr>
							{group.metrics.map((metric) => {
								// A custom row never expands, so a stale coordinate cannot
								// open one behind the panel it now opens instead.
								const openHere =
									expanded?.metricKey === metric.key &&
									!customMetricKeys.has(metric.key);
								// Muted rather than styled like data: the row is on
								// screen to be ACCOUNTED FOR, not to be read across, and
								// it must never compete with the numbers beside it.
								//
								// Only while the filter is OFF. With it on, the only empty
								// rows that survive are the user's own custom formulas
								// (see `metricGroupsWithoutEmptyRows`), and those render
								// exactly as they always have — the default view is the
								// one the user already knows.
								const empty = !hideEmptyRows && isEmptyMetricRow(metric);
								// A searched-for metric with nothing in any column says why
								// ACROSS the row, where its numbers would be (DOP-609) — one
								// sentence instead of a line of dashes. A custom row keeps its
								// cells: each still opens its formula.
								const reasonAcross =
									empty && !customMetricKeys.has(metric.key);
								return (
									<React.Fragment key={metric.key}>
										<tr
											data-testid={
												empty ? "kpi-empty-row" : undefined
											}
											className={`group hover:bg-gray-50 ${
												empty ? "bg-gray-50/60" : ""
											} ${
												dragging?.key === metric.key ? "opacity-50" : ""
											} ${
												dropHint?.kind === "row" && dropHint.key === metric.key
													? dropHint.where === "before"
														? "[&>*]:shadow-[inset_0_2px_0_#008782]"
														: "[&>*]:shadow-[inset_0_-2px_0_#008782]"
													: ""
											}`}
											onDragOver={(event) => {
												if (dragging?.kind !== "metric") return;
												event.preventDefault();
												const where = halfOf(event);
												if (
													dropHint?.kind !== "row" ||
													dropHint.key !== metric.key ||
													dropHint.where !== where
												) {
													setDropHint({ kind: "row", key: metric.key, where });
												}
											}}
											onDrop={(event) => {
												if (dragging?.kind !== "metric") return;
												event.preventDefault();
												onMoveMetric?.(
													dragging.key,
													halfOf(event) === "before"
														? { section: group.key, beforeKey: metric.key }
														: { section: group.key, afterKey: metric.key },
												);
												endDrag();
											}}
										>
											<th
												scope="row"
												className={`sticky left-0 z-10 border-b border-gray-100 px-3 py-1.5 text-left text-base font-normal ${
													empty
														? "bg-gray-50/60 text-text-muted"
														: "bg-white text-text-base"
												}`}
											>
												{onMoveMetric && (
													<button
														type="button"
														draggable
														data-move-handle={`m:${metric.key}`}
														aria-label={`Move ${metric.label}`}
														title="Drag to move, or use the ↑ ↓ keys"
														onDragStart={(event) => {
															event.dataTransfer.effectAllowed = "move";
															event.dataTransfer.setData("text/plain", metric.key);
															setDragging({ kind: "metric", key: metric.key });
														}}
														onDragEnd={endDrag}
														onKeyDown={(event) =>
															moveMetricByKey(event, metric.key, group.key)
														}
														className={`-ml-2 mr-1 align-middle ${HANDLE}`}
													>
														<GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
													</button>
												)}
												{metric.label}
												{onEditCustomMetric &&
													customMetricKeys.has(metric.key) && (
														<KpiCustomMetricEdit
															metricKey={metric.key}
															metricLabel={metric.label}
															onEdit={onEditCustomMetric}
														/>
													)}
												{onDeleteCustomMetric &&
													customMetricKeys.has(metric.key) && (
														<KpiCustomMetricDelete
															metricKey={metric.key}
															metricLabel={metric.label}
															busy={deletingMetricKey === metric.key}
															onDelete={onDeleteCustomMetric}
														/>
													)}
												{!metric.is_raw && (
													<span
														className="ml-2 text-xs text-text-muted"
														title="Computed from extracted values, not read from a file"
													>
														calc
													</span>
												)}
												{empty && !reasonAcross && (
													// The point of showing the row at all. A blank
													// that says why it is blank is accounted for;
													// one that says nothing reads as a bug.
													<span className="mt-0.5 block whitespace-normal text-xs font-normal text-text-muted">
														{EMPTY_ROW_MESSAGE}
													</span>
												)}
											</th>
											{reasonAcross ? (
												<td
													colSpan={table.periods.length}
													className="border-b border-gray-100 px-3 py-1.5 text-left text-xs italic text-[#8a9099]"
												>
													{/* Wrapped, never cut: this sentence is the only
													    thing the row says, and half of it reads as a
													    bug. Capped at a readable measure; `w-0
													    min-w-full` takes the columns' width as it is
													    instead of stretching them to fit the text. */}
													<span className="block w-0 min-w-full max-w-[720px] whitespace-normal">
														<span aria-hidden="true">— </span>
														<span>{EMPTY_ROW_MESSAGE}</span>
													</span>
												</td>
											) : table.periods.map((period, index) => (
												<KpiCellView
													key={period}
													cell={metric.cells[period]}
													unit={metric.unit}
													metricKey={metric.key}
													metricLabel={metric.label}
													period={period}
													periodLabel={periodLabels[index]}
													selected={
														openHere && expanded?.period === period
													}
													onSelect={handleSelect}
													onOpenDetails={handleOpenDetails}
												/>
											))}
										</tr>
										{/* The evidence row: spans the whole table, sits
										    directly under the number it explains, and exists
										    only while that number is the open one. */}
										{openHere && expanded && (
											<tr>
												<td
													colSpan={table.periods.length + 1}
													className="p-0"
												>
													<KpiCellExpansion
														metric={metric}
														period={expanded.period}
														grain={grain}
														cell={metric.cells[expanded.period]}
														onOpenDetails={() => onOpenDetails(expanded)}
														onClose={closeExpansion}
														onOverride={onOverride}
														onRevert={onRevert}
													/>
												</td>
											</tr>
										)}
									</React.Fragment>
								);
							})}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default KpiGrid;
